import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { FormSection, Question } from '../philData';
import { validateFormat, LINK_URL_PATTERN } from '../questionFormats';
import { CURRENCIES, CURRENCY_SYMBOLS, formatGrouped } from '../currencies';

/**
 * Number input that displays thousands separators when blurred but always
 * stores a clean, comma-free value. While focused the applicant edits the raw
 * digits (no commas jumping the cursor); on blur it shows the grouped form.
 * Used for `number` questions the admin flagged with `groupThousands`.
 */
function GroupedNumberInput({ value, onChange, readOnly }: {
  value: any; onChange: (v: string) => void; readOnly?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const raw = value == null ? '' : String(value);
  const display = focused || raw === '' ? raw : formatGrouped(raw);
  return (
    <input
      className="phil-input num"
      type="text"
      inputMode="decimal"
      placeholder="0"
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value.replace(/,/g, ''))}
      disabled={readOnly}
    />
  );
}

/** Renders one application form section. Schema is fetched from the backend. */
export default function ApplicationSection() {
  const { sectionNum } = useParams<{ sectionNum: string }>();
  const navigate = useNavigate();

  const { data: schemaData, loading, error } = useApi(() => API.applicationSchema());
  const { data: reviewData } = useApi(() => API.applicationReview());
  const locked = !!reviewData?.submitted;

  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [answersLoaded, setAnswersLoaded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formatBlocked, setFormatBlocked] = useState(false);

  const sections: FormSection[] = schemaData?.sections ?? [];
  const index = clampIndex(sectionNum, sections.length);
  const section = sections[index - 1];

  // Load saved answers whenever the section changes.
  useEffect(() => {
    if (!section) return;
    setAnswersLoaded(false);
    API.getSectionAnswers(section.id)
      .then((data: any) => {
        if (data?.answers) {
          // Keys come back as strings; convert to numbers to match question ids.
          const parsed: Record<number, any> = {};
          for (const [k, v] of Object.entries(data.answers)) {
            parsed[Number(k)] = v;
          }
          setAnswers(parsed);
        }
      })
      .catch(() => {})
      .finally(() => setAnswersLoaded(true));
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !schemaData?.sections?.length) {
    return <div className="phil-error">Unable to load application form. {error}</div>;
  }
  if (!answersLoaded) return <div className="phil-loading">Loading…</div>;

  const setAnswer = (qid: number, value: any) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
    setSaved(false);
    setFormatBlocked(false);
  };

  // Text questions whose current answer is a non-empty string failing the
  // admin-configured format pattern. Blocks forward navigation (server re-checks
  // at submit). Blank answers pass here; required-ness is handled separately.
  const formatErrors = (): Question[] =>
    (section?.questions ?? []).filter(q =>
      q.type === 'text' && q.pattern &&
      typeof answers[q.id] === 'string' && answers[q.id] !== '' &&
      !validateFormat(q.pattern, answers[q.id]),
    );

  const persist = async () => {
    if (locked) return; // submitted applications are read-only
    setSaving(true);
    try {
      await API.saveSection(section.id, answers);
    } finally {
      setSaving(false);
    }
  };

  const onSaveDraft = async () => {
    await persist();
    setSaved(true);
  };

  const onContinue = async () => {
    if (formatErrors().length) { setFormatBlocked(true); return; }
    await persist();
    if (index < sections.length) {
      navigate(`/apply/section-${index + 1}`);
    } else {
      navigate('/apply/review');
    }
  };

  const onBack = () => {
    if (index > 1) navigate(`/apply/section-${index - 1}`);
    else navigate('/apply');
  };

  // Jump straight to another step. `target` is 1..sections.length for a section,
  // or sections.length + 1 for the Review step. Persists the current draft first
  // so the applicant doesn't lose unsaved input (matches Save & Continue).
  const onJump = async (target: number) => {
    if (target === index) return;
    await persist();
    if (target > sections.length) navigate('/apply/review');
    else navigate(`/apply/section-${target}`);
  };

  return (
    <>
      <StepProgress current={index} sections={sections} onJump={onJump} reviewSections={reviewData?.sections} />

      <div className="phil-section-title-row">
        <h3>{section.title}</h3>
        <span className="phil-section-count">({section.questionCount} questions)</span>
        <span className="phil-required-legend">
          <span className="phil-q-req">*</span> = Required
        </span>
      </div>

      {locked && (
        <div className="phil-saved-banner" style={{ marginBottom: 12 }}>
          ✓ Your application has been submitted. These answers are read-only.
        </div>
      )}

      <div className="phil-card">
        <div style={{ padding: 20 }}>
          <div className="phil-form-scroll">
            {section.questions.map((q, idx) => (
              <FieldRenderer
                key={q.id}
                q={q}
                num={idx + 1}
                value={answers[q.id]}
                onChange={v => setAnswer(q.id, v)}
                readOnly={locked}
              />
            ))}
          </div>

          {formatBlocked && (
            <div className="phil-error" style={{ marginTop: 12 }}>
              Some answers don’t match the required format. Please fix the highlighted fields before continuing.
            </div>
          )}

          <div className="phil-form-actions">
            <button className="phil-btn phil-btn-secondary" onClick={onBack}>
              ← {index > 1 ? 'Back' : 'Dashboard'}
            </button>
            {locked ? (
              <button
                className="phil-btn phil-btn-primary"
                onClick={() => navigate(index < sections.length ? `/apply/section-${index + 1}` : '/apply/review')}
              >
                {index < sections.length ? 'Next →' : 'Back to Review →'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="phil-btn phil-btn-secondary" onClick={onSaveDraft} disabled={saving}>
                  {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Draft'}
                </button>
                <button className="phil-btn phil-btn-primary" onClick={onContinue} disabled={saving}>
                  {index < sections.length ? 'Save & Continue →' : 'Continue to Review →'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function clampIndex(raw: string | undefined, total: number): number {
  const n = parseInt((raw || '1').replace('section-', ''), 10);
  if (Number.isNaN(n) || n < 1) return 1;
  if (n > total) return total;
  return n;
}

function StepProgress({ current, sections, onJump, reviewSections }: {
  current: number; sections: FormSection[]; onJump: (target: number) => void;
  reviewSections?: { id: string; status: string }[];
}) {
  const reviewNum = sections.length + 1;
  // A section shows a checkmark only once its required questions are all
  // answered (status "Complete" from the review endpoint), not merely because
  // the applicant has navigated past it.
  const completeById: Record<string, boolean> = {};
  for (const rs of reviewSections ?? []) completeById[rs.id] = rs.status === 'Complete';
  return (
    <div className="phil-steps">
      {sections.map((s: FormSection, i: number) => {
        const n = i + 1;
        const state = completeById[s.id] ? 'done' : n === current ? 'current' : 'future';
        return (
          <React.Fragment key={s.id}>
            <button
              type="button"
              className="phil-step phil-step-clickable"
              onClick={() => onJump(n)}
              aria-current={n === current ? 'step' : undefined}
            >
              <div className={`phil-step-dot ${state}`}>{state === 'done' ? '✓' : n}</div>
              <span className={`phil-step-label ${state}`}>{s.shortLabel}</span>
            </button>
            <div className="phil-step-conn" />
          </React.Fragment>
        );
      })}
      <button
        type="button"
        className="phil-step phil-step-clickable"
        onClick={() => onJump(reviewNum)}
      >
        <div className="phil-step-dot future">{reviewNum}</div>
        <span className="phil-step-label future">Review</span>
      </button>
    </div>
  );
}

function FieldRenderer({ q, num, value, onChange, readOnly }: {
  q: Question; num: number; value: any; onChange: (v: any) => void; readOnly?: boolean;
}) {
  const label = (
    <div className="phil-q-label">
      <span className="phil-q-num">{num}.</span>
      {q.label}
      {q.required && <span className="phil-q-req">*</span>}
    </div>
  );

  // Format enforcement: flag a non-empty value that fails the pattern.
  // `text` uses an admin-set pattern; `link` always validates as a URL (falling
  // back to the built-in URL preset when the admin hasn't set a custom pattern).
  const effectivePattern = q.type === 'link' ? (q.pattern || LINK_URL_PATTERN)
    : q.type === 'text' ? q.pattern
    : undefined;
  const formatInvalid = !!effectivePattern &&
    typeof value === 'string' && value !== '' && !validateFormat(effectivePattern, value);

  let control: React.ReactNode;
  switch (q.type) {
    case 'text':
      control = (
        <input
          className="phil-input"
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={readOnly}
          style={formatInvalid ? { borderColor: '#E24B4A' } : undefined}
        />
      );
      break;
    case 'link':
      // Applicant enters a plain URL; the evaluator view renders it as a link.
      control = (
        <input
          className="phil-input"
          type="url"
          placeholder="https://…"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={readOnly}
          style={formatInvalid ? { borderColor: '#E24B4A' } : undefined}
        />
      );
      break;
    case 'number':
      control = q.groupThousands
        ? <GroupedNumberInput value={value} onChange={onChange} readOnly={readOnly} />
        : <input className="phil-input num" type="number" placeholder="0" value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={readOnly} />;
      break;
    case 'textarea':
      control = <textarea className="phil-textarea" value={value || ''} onChange={e => onChange(e.target.value)} disabled={readOnly} />;
      break;
    case 'currency': {
      // Stored as { amount, currency }. Applicant picks the currency; amount is a whole number.
      const cur = (value && typeof value === 'object') ? value : {};
      const code = cur.currency || CURRENCIES[0];
      const emit = (patch: { amount?: number | null; currency?: string }) =>
        onChange({ amount: cur.amount ?? null, currency: code, ...patch });
      control = (
        <div className="phil-currency">
          <select
            className="phil-select"
            value={code}
            onChange={e => emit({ currency: e.target.value })}
            disabled={readOnly}
            title={CURRENCY_SYMBOLS[code] ? `${code} (${CURRENCY_SYMBOLS[code].trim()})` : code}
          >
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <GroupedNumberInput
            value={cur.amount}
            onChange={raw => emit({ amount: raw === '' ? null : Math.round(Number(raw)) })}
            readOnly={readOnly}
          />
        </div>
      );
      break;
    }
    case 'select':
      control = q.searchable ? (
        <SearchableSelect
          options={q.options ?? []}
          value={value || ''}
          onChange={onChange}
          readOnly={readOnly}
        />
      ) : (
        <select className="phil-select" value={value || ''} onChange={e => onChange(e.target.value)} disabled={readOnly}>
          <option value="">Select…</option>
          {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      break;
    case 'checkboxes': {
      const selected: string[] = Array.isArray(value) ? value : [];
      const toggle = (opt: string) => {
        onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt]);
      };
      control = (
        <div className="phil-checks">
          {q.options?.map(o => (
            <label key={o} className="phil-check">
              <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} disabled={readOnly} />
              {o}
            </label>
          ))}
        </div>
      );
      break;
    }
    case 'file':
      control = <FileField q={q} value={value} onChange={onChange} readOnly={readOnly} />;
      break;
    case 'video':
      control = <FileField q={q} value={value} onChange={onChange} allowUrl readOnly={readOnly} />;
      break;
    default:
      control = null;
  }

  return (
    <div className="phil-form-field">
      {label}
      {control}
      {q.note && <div className="phil-note">{q.note}</div>}
      {formatInvalid && (
        <div className="phil-note" style={{ color: '#E24B4A' }}>
          {q.patternHint || (q.type === 'link'
            ? 'Enter a valid URL, e.g. example.com or https://example.com'
            : 'This answer does not match the required format.')}
        </div>
      )}
    </div>
  );
}

/** Type-to-filter dropdown for `select` questions flagged searchable in
 *  /admin/questions. Behaves like a native <select> — the committed answer is
 *  always one of `options` (or ''), free-typed text only filters the list — but
 *  lets applicants narrow a long option list by typing. Keyboard: ↑/↓ to move,
 *  Enter to pick, Esc to close. */
function SearchableSelect({ options, value, onChange, readOnly }: {
  options: string[]; value: string; onChange: (v: string) => void; readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // While closed, the input shows the committed value; while open, it shows the
  // in-progress filter text.
  const display = open ? query : value;
  const filtered = query.trim()
    ? options.filter(o => o.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // Close (and discard any uncommitted filter text) when clicking away.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const commit = (opt: string) => {
    onChange(opt);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive(a => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[active]) { e.preventDefault(); commit(filtered[active]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="phil-searchable-select" ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="phil-input"
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        placeholder="Type to search…"
        value={display}
        disabled={readOnly}
        onFocus={() => { setOpen(true); setActive(0); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
      />
      {value && !readOnly && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={() => commit('')}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#999',
          }}
        >
          ×
        </button>
      )}
      {open && !readOnly && (
        <div
          role="listbox"
          style={{
            position: 'absolute', zIndex: 20, top: 'calc(100% + 2px)', left: 0, right: 0,
            maxHeight: 240, overflowY: 'auto', background: '#fff',
            border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>No matches</div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o}
                role="option"
                aria-selected={o === value}
                onMouseDown={e => { e.preventDefault(); commit(o); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 14,
                  background: i === active ? '#f0f4ff' : o === value ? '#f7f7f7' : '#fff',
                  fontWeight: o === value ? 600 : 400,
                }}
              >
                {o}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface FileDescriptor { key: string; name: string; contentType?: string; size?: number; }

/** Direct browser→S3 upload via a backend-issued presigned URL.
 *  Stores a descriptor ({key,name,...}) as the answer. For `video`, also accepts a URL. */
function FileField({ q, value, onChange, allowUrl, readOnly }: {
  q: Question; value: any; onChange: (v: any) => void; allowUrl?: boolean; readOnly?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const descriptor: FileDescriptor | null = value && typeof value === 'object' ? value : null;
  const urlValue: string = typeof value === 'string' ? value : '';

  const upload = async (file: File) => {
    setErr('');
    setUploading(true);
    setProgress(0);
    try {
      const res = await API.uploadUrl(q.id, file.name, file.type || 'application/octet-stream');
      if (!res?.uploadUrl) throw new Error('no-url');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(res.method || 'PUT', res.uploadUrl);
        Object.entries(res.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, String(v)));
        xhr.upload.onprogress = e => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`s3-${xhr.status}`));
        xhr.onerror = () => reject(new Error('network'));
        xhr.send(file);
      });
      onChange({ ...res.descriptor, size: file.size });
    } catch (e: any) {
      const msg = String(e?.message || '');
      setErr(msg.includes('503')
        ? 'File uploads aren’t enabled in this environment yet.'
        : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (files: FileList | null) => { if (files && files[0]) upload(files[0]); };

  const viewFile = async () => {
    if (!descriptor?.key) return;
    try {
      const res = await API.fileUrl(descriptor.key);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch { /* ignore */ }
  };

  return (
    <div>
      {descriptor ? (
        <div className="phil-file-chip">
          <span className="phil-file-name">📎 {descriptor.name}</span>
          <button type="button" className="phil-file-link" onClick={viewFile}>View</button>
          {!readOnly && (
            <button type="button" className="phil-file-remove" onClick={() => onChange(undefined)}>Remove</button>
          )}
        </div>
      ) : readOnly ? (
        <div className="phil-upload" style={{ opacity: 0.6 }}>
          {allowUrl && urlValue
            ? <a href={urlValue} target="_blank" rel="noopener noreferrer">{urlValue}</a>
            : <>No file provided</>}
        </div>
      ) : (
        <div
          className={`phil-upload phil-upload-interactive${dragOver ? ' dragover' : ''}`}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); if (!uploading) onPick(e.dataTransfer.files); }}
        >
          {uploading
            ? <>⏳ Uploading… {progress}%</>
            : <>{allowUrl ? '▶' : '📎'} Click to upload or drag &amp; drop</>}
          {q.hint && <span className="hint">{q.hint}</span>}
          <input
            ref={inputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={e => onPick(e.target.files)}
          />
        </div>
      )}

      {err && <div className="phil-file-err">{err}</div>}

      {allowUrl && !descriptor && !readOnly && (
        <input
          className="phil-input"
          style={{ marginTop: 10 }}
          type="text"
          placeholder="…or paste a URL (https://…)"
          value={urlValue}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
